import dayjs from 'dayjs';
import { DateFormat } from '../constants/data';
import type { Season } from '../types';

export const getDatesBetween = (startDate: string | number | Date, endDate: string | number | Date): string[] => {
  const dates: string[] = [];
  const currentDate = new Date(startDate);
  const removalDay = new Date(endDate);
  while (currentDate.getTime() <= removalDay.getTime()) {
    dates.push(dayjs(currentDate).format(DateFormat));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dates;
};

export const getClosestDate = (dates: string[], targetDate: Date): string | undefined => {
  let i = 0;
  while (i < dates.length && new Date(dates[i]).getTime() - targetDate.getTime() > 0) {
    i = i + 1;
  }
  return dates[i];
};

export const isDateInPeriod = (
  startDate: dayjs.ConfigType,
  endDate: dayjs.ConfigType,
  dateToCheck: dayjs.ConfigType,
): boolean => {
  const end = endDate ? endDate : dayjs();
  return (
    (dayjs(dateToCheck).isAfter(startDate) || dayjs(dateToCheck).isSame(startDate)) &&
    (dayjs(dateToCheck).isBefore(end) || dayjs(dateToCheck).isSame(end))
  );
};

export const convertMinutesToHoursAndMinutes = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return hours ? `${hours} h ${remainingMinutes} min` : `${remainingMinutes} min`;
};

export const convertMinutesToHours = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  return `${hours} h`;
};

export const convertMinutesToMinutes = (minutes: number): string => {
  const m = minutes.toFixed();
  return `${m} m`;
};

export const convertSecondsToHoursAndMinutes = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return hours ? `${hours} h ${remainingMinutes} min` : `${remainingMinutes} min`;
};

export const convertSecondsToHoursAndMinutesArray = (seconds: number): [number, number] => {
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return [hours, remainingMinutes];
};

export const convertSecondsToHours = (seconds: number): number => {
  const minutes = Math.floor(seconds / 60);
  return Math.round(minutes / 60);
};

export const groupDataByMonth = <T extends { date: string }>(data: T[]): { month: string; data: T[] }[] => {
  const grouped = data.reduce<Record<string, T[]>>((acc, dateStr) => {
    const date = new Date(dateStr.date);
    const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;

    if (!acc[yearMonth]) {
      acc[yearMonth] = [];
    }

    acc[yearMonth].push(dateStr);
    return acc;
  }, {});

  return Object.entries(grouped).map(([month, data]) => ({ month, data }));
};
